//create a abstract class for crud operations uses api service like in document service
import { Observable, throwError, of } from 'rxjs';
import { inject } from '@angular/core';
import { switchMap, take } from 'rxjs/operators';
import { ApiService, QueryParams } from '../api/api.service';
import { buildResource } from '../utils/api-resource.utils';
import { Role } from '../model';

export enum ServiceTransactionsEnum {
  GET_ALL = 'get-all',
  GET_BY_ID = 'get-by-id',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
}

export interface RoleRequirements {
  getAll?: Role[];
  getById?: Role[];
  create?: Role[];
  update?: Role[];
  delete?: Role[];
}

export abstract class AbstractCrudOperations<T, CreateCommandDto, UpdateCommandDto extends { id: string }> {
  protected readonly resource!: string;
  protected abstract readonly transactions: Record<ServiceTransactionsEnum, string>;
  protected readonly apiService: ApiService = inject(ApiService);
  protected authService?: any; // Will be injected by subclasses if needed
  protected roleRequirements?: RoleRequirements;

  protected checkRole(requiredRoles?: Role[]): Observable<boolean> {
    if (!requiredRoles || requiredRoles.length === 0 || !this.authService) {
      return of(true);
    }

    return this.authService.getUserClaims().pipe(
      take(1),
      switchMap((claims: any) => {
        if (!claims) {
          return throwError(() => new Error('Insufficient permissions'));
        }
        const roles = claims['cognito:groups'] || claims.roles || [];
        const userRoles = Array.isArray(roles) ? roles : [roles];
        const hasRole = userRoles.some((role: any) => requiredRoles.includes(role));

        if (!hasRole) {
          return throwError(() => new Error('Insufficient permissions'));
        }
        return of(true);
      })
    );
  }

  getAll(queryParams?: QueryParams, transaction?: string): Observable<T[]> {
    return this.checkRole(this.roleRequirements?.getAll).pipe(
      switchMap(() => {
        const resource = buildResource(queryParams, this.resource);
        return this.apiService.get<T[]>(resource, transaction || this.transactions[ServiceTransactionsEnum.GET_ALL]);
      })
    );
  }

  getById(id: string, transaction?: string, queryParams?: QueryParams): Observable<T> {
    return this.checkRole(this.roleRequirements?.getById).pipe(
      switchMap(() => {
        const resource = buildResource(queryParams, `${this.resource}/${id}`);
        return this.apiService.get<T>(resource, transaction || this.transactions[ServiceTransactionsEnum.GET_BY_ID]);
      })
    );
  }

  create(body: CreateCommandDto, transaction?: string, queryParams?: QueryParams): Observable<T> {
    return this.checkRole(this.roleRequirements?.create).pipe(
      switchMap(() => {
        const resource = buildResource(queryParams, this.resource);
        return this.apiService.post<T>(
          resource,
          body,
          transaction || this.transactions[ServiceTransactionsEnum.CREATE]
        );
      })
    );
  }

  update(item: UpdateCommandDto, transaction?: string, queryParams?: QueryParams): Observable<T> {
    return this.checkRole(this.roleRequirements?.update).pipe(
      switchMap(() => {
        const resource = buildResource(queryParams, `${this.resource}/${item.id}`);
        return this.apiService.put<UpdateCommandDto, T>(
          resource,
          item,
          transaction || this.transactions[ServiceTransactionsEnum.UPDATE]
        );
      })
    );
  }

  delete(id: string, transaction?: string, queryParams?: QueryParams, body?: T): Observable<string> {
    return this.checkRole(this.roleRequirements?.delete).pipe(
      switchMap(() => {
        const resource = buildResource(queryParams, `${this.resource}/${id}`);
        return this.apiService.delete<T>(
          resource,
          body,
          transaction || this.transactions[ServiceTransactionsEnum.DELETE]
        );
      })
    );
  }
}
